import React, { useState, useCallback } from 'react';
import Cropper from 'react-easy-crop';
import { motion } from 'framer-motion';
import { FaTimes, FaCheck, FaSearchPlus, FaSearchMinus } from 'react-icons/fa';

/**
 * Crops an image from a data URL given the pixel crop area.
 * Returns a Blob of the cropped circular-safe square image.
 */
const getCroppedImg = (imageSrc, pixelCrop) => {
  return new Promise((resolve, reject) => {
    const image = new Image();
    image.crossOrigin = 'anonymous';
    image.onload = () => {
      const canvas = document.createElement('canvas');
      const size = Math.min(pixelCrop.width, pixelCrop.height);
      canvas.width = size;
      canvas.height = size;
      const ctx = canvas.getContext('2d');

      ctx.drawImage(
        image,
        pixelCrop.x,
        pixelCrop.y,
        pixelCrop.width,
        pixelCrop.height,
        0,
        0,
        size,
        size
      );

      canvas.toBlob(
        (blob) => {
          if (!blob) return reject(new Error('Canvas is empty'));
          resolve(blob);
        },
        'image/jpeg',
        0.92
      );
    };
    image.onerror = reject;
    image.src = imageSrc;
  });
};

const AvatarCropModal = ({ imageSrc, onClose, onCropDone }) => {
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);
  const [saving, setSaving] = useState(false);

  const onCropComplete = useCallback((_, croppedPixels) => {
    setCroppedAreaPixels(croppedPixels);
  }, []);

  const handleSave = async () => {
    if (!croppedAreaPixels) return;
    setSaving(true);
    try {
      const croppedBlob = await getCroppedImg(imageSrc, croppedAreaPixels);
      const file = new File([croppedBlob], 'avatar.jpg', { type: 'image/jpeg' });
      await onCropDone(file);
    } catch (err) {
      console.error('Crop failed', err);
    } finally {
      setSaving(false);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 bg-black/80 z-[60] flex flex-col items-center justify-center"
      data-testid="avatar-crop-modal"
    >
      {/* Header */}
      <div className="w-full max-w-md px-4 py-3 flex items-center justify-between">
        <button
          onClick={onClose}
          className="text-white/70 hover:text-white p-2 rounded-full hover:bg-white/10 transition-colors"
          data-testid="crop-cancel-btn"
        >
          <FaTimes size={18} />
        </button>
        <span className="text-white font-bold text-base">Adjust Photo</span>
        <button
          onClick={handleSave}
          disabled={saving}
          className="bg-[#4A90E2] text-white px-4 py-1.5 rounded-full text-sm font-bold hover:bg-[#3A80D2] transition-colors disabled:opacity-50 flex items-center space-x-1.5"
          data-testid="crop-save-btn"
        >
          {saving ? (
            <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
          ) : (
            <>
              <FaCheck size={12} />
              <span>Save</span>
            </>
          )}
        </button>
      </div>

      {/* Crop area */}
      <div className="relative w-full max-w-md aspect-square">
        <Cropper
          image={imageSrc}
          crop={crop}
          zoom={zoom}
          aspect={1}
          cropShape="round"
          showGrid={false}
          onCropChange={setCrop}
          onZoomChange={setZoom}
          onCropComplete={onCropComplete}
        />
      </div>

      {/* Zoom slider */}
      <div className="w-full max-w-md px-6 py-4 flex items-center space-x-3">
        <FaSearchMinus className="text-white/50" size={14} />
        <input
          type="range"
          min={1}
          max={3}
          step={0.05}
          value={zoom}
          onChange={(e) => setZoom(Number(e.target.value))}
          className="flex-1 accent-[#4A90E2] h-1"
          data-testid="crop-zoom-slider"
        />
        <FaSearchPlus className="text-white/50" size={14} />
      </div>
    </motion.div>
  );
};

export default AvatarCropModal;
